import { AxiosError } from 'axios';

export interface ErrorInfo {
  message: string;
  code?: string | number;
  field?: string;
  details?: any;
  severity: 'low' | 'medium' | 'high' | 'critical';
  action?: 'retry' | 'reload' | 'contact_support' | 'redirect' | 'none';
  timestamp: Date;
  context?: Record<string, any>;
}

export interface ValidationError {
  field: string;
  message: string;
  value?: any;
}

export interface ApiErrorResponse {
  error: string;
  message?: string;
  details?: string;
  validation_errors?: ValidationError[];
  code?: string | number;
  status?: number;
}

export interface ProcessedError {
  title: string;
  message: string;
  severity: ErrorInfo['severity'];
  action: ErrorInfo['action'];
  code?: string | number;
  validationErrors?: ValidationError[];
  originalError?: any;
  canRetry: boolean;
  shouldNotify: boolean;
  context?: Record<string, any>;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorLog: ErrorInfo[] = [];
  private maxLogSize = 100;

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  // Process different types of errors
  processError(error: any, context?: Record<string, any>): ProcessedError {
    const errorInfo = this.analyzeError(error, context);
    this.logError(errorInfo);

    return {
      title: this.getErrorTitle(errorInfo),
      message: this.getUserFriendlyMessage(errorInfo),
      severity: errorInfo.severity,
      action: errorInfo.action,
      code: errorInfo.code,
      validationErrors: this.extractValidationErrors(error),
      originalError: error,
      canRetry: this.canRetry(errorInfo),
      shouldNotify: this.shouldNotify(errorInfo),
      context: errorInfo.context,
    };
  }

  // Analyze error and extract information
  private analyzeError(error: any, context?: Record<string, any>): ErrorInfo {
    const timestamp = new Date();

    // Network/Connection errors
    if (this.isNetworkError(error)) {
      return {
        message: 'Network connection failed',
        severity: 'high',
        action: 'retry',
        timestamp,
        context,
      };
    }

    // Axios HTTP errors
    if (this.isAxiosError(error)) {
      return this.processAxiosError(error, context, timestamp);
    }

    // Validation errors
    if (this.isValidationError(error)) {
      return {
        message: error.message || 'Validation failed',
        severity: 'medium',
        action: 'none',
        timestamp,
        context,
      };
    }

    // Authentication errors
    if (this.isAuthError(error)) {
      return {
        message: 'Authentication required',
        code: 'AUTH_REQUIRED',
        severity: 'high',
        action: 'redirect',
        timestamp,
        context,
      };
    }

    // Permission errors
    if (this.isPermissionError(error)) {
      return {
        message: 'Insufficient permissions',
        code: 'PERMISSION_DENIED',
        severity: 'medium',
        action: 'contact_support',
        timestamp,
        context,
      };
    }

    // Generic errors
    return {
      message: error.message || 'An unexpected error occurred',
      severity: 'medium',
      action: 'retry',
      timestamp,
      context,
    };
  }

  // Process Axios errors specifically
  private processAxiosError(error: AxiosError, context?: Record<string, any>, timestamp: Date = new Date()): ErrorInfo {
    const response = error.response;
    const status = response?.status;
    const data = response?.data as ApiErrorResponse;

    // Server errors (5xx)
    if (status && status >= 500) {
      return {
        message: 'Server error occurred',
        code: status,
        severity: 'high',
        action: 'retry',
        timestamp,
        context: { ...context, status, url: error.config?.url },
      };
    }

    // Client errors (4xx)
    if (status && status >= 400) {
      return this.processClientError(status, data, error, context, timestamp);
    }

    // Request timeout
    if (error.code === 'ECONNABORTED') {
      return {
        message: 'Request timeout',
        code: 'TIMEOUT',
        severity: 'medium',
        action: 'retry',
        timestamp,
        context,
      };
    }

    // Network error
    return {
      message: 'Network error',
      code: 'NETWORK_ERROR',
      severity: 'high',
      action: 'retry',
      timestamp,
      context,
    };
  }

  // Process client errors (4xx)
  private processClientError(
    status: number,
    data: ApiErrorResponse,
    error: AxiosError,
    context?: Record<string, any>,
    timestamp: Date = new Date()
  ): ErrorInfo {
    switch (status) {
      case 400:
        return {
          message: data?.error || 'Invalid request',
          code: 'BAD_REQUEST',
          severity: 'medium',
          action: 'none',
          timestamp,
          context: { ...context, validationErrors: data?.validation_errors },
        };

      case 401:
        return {
          message: 'Please sign in to continue',
          code: 'UNAUTHORIZED',
          severity: 'high',
          action: 'redirect',
          timestamp,
          context,
        };

      case 403:
        return {
          message: 'You do not have permission to perform this action',
          code: 'FORBIDDEN',
          severity: 'medium',
          action: 'contact_support',
          timestamp,
          context,
        };

      case 404:
        return {
          message: 'The requested resource was not found',
          code: 'NOT_FOUND',
          severity: 'medium',
          action: 'reload',
          timestamp,
          context,
        };

      case 409:
        return {
          message: data?.error || 'Conflict with existing data',
          code: 'CONFLICT',
          severity: 'medium',
          action: 'none',
          timestamp,
          context,
        };

      case 422:
        return {
          message: data?.error || 'Data validation failed',
          code: 'VALIDATION_ERROR',
          severity: 'medium',
          action: 'none',
          timestamp,
          context: { ...context, validationErrors: data?.validation_errors },
        };

      case 429:
        return {
          message: 'Too many requests. Please try again later',
          code: 'RATE_LIMITED',
          severity: 'medium',
          action: 'retry',
          timestamp,
          context,
        };

      default:
        return {
          message: data?.error || 'Request failed',
          code: status,
          severity: 'medium',
          action: 'retry',
          timestamp,
          context,
        };
    }
  }

  // Error type checkers
  private isNetworkError(error: any): boolean {
    return (
      !error.response &&
      (error.code === 'NETWORK_ERROR' ||
        error.message?.includes('Network Error') ||
        error.message?.includes('ERR_NETWORK'))
    );
  }

  private isAxiosError(error: any): error is AxiosError {
    return error.isAxiosError || (error.response && error.config);
  }

  private isValidationError(error: any): boolean {
    return (
      error.name === 'ValidationError' ||
      error.type === 'validation' ||
      (error.response?.status === 422)
    );
  }

  private isAuthError(error: any): boolean {
    return (
      error.response?.status === 401 ||
      error.code === 'UNAUTHORIZED' ||
      error.message?.includes('unauthorized')
    );
  }

  private isPermissionError(error: any): boolean {
    return (
      error.response?.status === 403 ||
      error.code === 'FORBIDDEN' ||
      error.message?.includes('permission')
    );
  }

  // Get user-friendly error title
  private getErrorTitle(errorInfo: ErrorInfo): string {
    switch (errorInfo.severity) {
      case 'critical':
        return 'Critical Error';
      case 'high':
        return 'Error';
      case 'medium':
        return 'Warning';
      case 'low':
        return 'Notice';
      default:
        return 'Error';
    }
  }

  // Get user-friendly error message
  private getUserFriendlyMessage(errorInfo: ErrorInfo): string {
    // Map common technical errors to user-friendly messages
    const friendlyMessages: Record<string, string> = {
      'Network Error': 'Unable to connect to the server. Please check your internet connection.',
      'Request timeout': 'The request took too long to complete. Please try again.',
      'Server error occurred': 'Our servers are experiencing issues. Please try again in a few minutes.',
      'Authentication required': 'Your session has expired. Please sign in again.',
      'Insufficient permissions': 'You do not have permission to perform this action.',
      'The requested resource was not found': 'The item you are looking for could not be found.',
      'Rate limited': 'You are making requests too quickly. Please wait a moment and try again.',
    };

    return friendlyMessages[errorInfo.message] || errorInfo.message;
  }

  // Extract validation errors from error response
  private extractValidationErrors(error: any): ValidationError[] | undefined {
    if (error.response?.data?.validation_errors) {
      return error.response.data.validation_errors;
    }

    if (error.validationErrors) {
      return error.validationErrors;
    }

    return undefined;
  }

  // Determine if error can be retried
  private canRetry(errorInfo: ErrorInfo): boolean {
    return errorInfo.action === 'retry';
  }

  // Determine if error should trigger notification
  private shouldNotify(errorInfo: ErrorInfo): boolean {
    return ['medium', 'high', 'critical'].includes(errorInfo.severity);
  }

  // Log error for debugging and monitoring
  private logError(errorInfo: ErrorInfo): void {
    // Add to in-memory log
    this.errorLog.unshift(errorInfo);
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.group(`🚨 Error [${errorInfo.severity.toUpperCase()}]`);
      console.error('Message:', errorInfo.message);
      if (errorInfo.code) console.error('Code:', errorInfo.code);
      if (errorInfo.context) console.error('Context:', errorInfo.context);
      console.error('Timestamp:', errorInfo.timestamp.toISOString());
      console.groupEnd();
    }

    // Send to monitoring service in production
    if (process.env.NODE_ENV === 'production' && errorInfo.severity === 'critical') {
      this.sendToMonitoring(errorInfo);
    }
  }

  // Send error to monitoring service
  private sendToMonitoring(errorInfo: ErrorInfo): void {
    try {
      // Import monitoring service dynamically to avoid circular dependencies
      import('./monitoring').then(({ monitoring }) => {
        const processedError: ProcessedError = {
          title: this.getErrorTitle(errorInfo),
          message: this.getUserFriendlyMessage(errorInfo),
          severity: errorInfo.severity,
          action: errorInfo.action,
          code: errorInfo.code,
          originalError: errorInfo,
          canRetry: this.canRetry(errorInfo),
          shouldNotify: this.shouldNotify(errorInfo),
          context: errorInfo.context,
        };
        
        monitoring.logError(processedError, {
          source: 'error_handler',
          timestamp: errorInfo.timestamp.toISOString(),
        });
      }).catch(monitoringError => {
        console.error('Failed to send error to monitoring service:', monitoringError);
      });
    } catch (error) {
      console.error('Failed to load monitoring service:', error);
    }
  }

  // Get recent errors for debugging
  getRecentErrors(limit: number = 10): ErrorInfo[] {
    return this.errorLog.slice(0, limit);
  }

  // Clear error log
  clearErrorLog(): void {
    this.errorLog = [];
  }

  // Get error statistics
  getErrorStats(): {
    total: number;
    bySeverity: Record<string, number>;
    byAction: Record<string, number>;
    recent: number;
  } {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
    const recent = this.errorLog.filter(error => error.timestamp > oneHourAgo).length;

    const bySeverity = this.errorLog.reduce((acc, error) => {
      acc[error.severity] = (acc[error.severity] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const byAction = this.errorLog.reduce((acc, error) => {
      if (error.action) {
        acc[error.action] = (acc[error.action] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    return {
      total: this.errorLog.length,
      bySeverity,
      byAction,
      recent,
    };
  }
}

// Utility functions for common error handling patterns

export const handleApiError = (error: any, context?: Record<string, any>): ProcessedError => {
  return ErrorHandler.getInstance().processError(error, context);
};

export const handleFormError = (error: any, setFieldError: (field: string, message: string) => void): ProcessedError => {
  const processed = handleApiError(error, { type: 'form_submission' });
  
  // Set field-specific errors if available
  if (processed.validationErrors) {
    processed.validationErrors.forEach(validationError => {
      setFieldError(validationError.field, validationError.message);
    });
  }

  return processed;
};

export const handleAsyncError = async <T>(
  operation: () => Promise<T>,
  context?: Record<string, any>
): Promise<{ data?: T; error?: ProcessedError }> => {
  try {
    const data = await operation();
    return { data };
  } catch (error) {
    const processedError = handleApiError(error, { ...context, type: 'async_operation' });
    return { error: processedError };
  }
};

export const retryOperation = async <T>(
  operation: () => Promise<T>,
  maxRetries: number = 3,
  delay: number = 1000,
  context?: Record<string, any>
): Promise<{ data?: T; error?: ProcessedError }> => {
  let lastError: any;

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      const data = await operation();
      return { data };
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries) {
        break;
      }

      const processed = handleApiError(error, { ...context, attempt });
      if (!processed.canRetry) {
        break;
      }

      // Wait before retrying with exponential backoff
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, attempt - 1)));
    }
  }

  const processedError = handleApiError(lastError, { ...context, type: 'retry_failed', maxRetries });
  return { error: processedError };
};

// Error boundary helpers
export const getErrorBoundaryFallback = (error: Error, errorInfo: any) => {
  const processed = handleApiError(error, { 
    type: 'error_boundary',
    componentStack: errorInfo.componentStack,
  });

  return {
    title: 'Something went wrong',
    message: processed.message,
    canReload: true,
    canGoBack: true,
  };
};

export default ErrorHandler;